export type SignalLevel = 'strong-buy' | 'buy' | 'neutral' | 'sell' | 'strong-sell';
export type Confidence = 'low' | 'medium' | 'high';
export type Timeframe = '1h' | '4h' | '1d';

export interface PricePoint {
  t: number; // ms epoch
  price: number;
}

export interface SignalResult {
  level: SignalLevel;
  score: number;
  rsi: number;
  smaShort: number;
  smaLong: number;
  momentumPct: number;
  reasons: string[];
  confidence: Confidence;
  dataPoints: number;
}

export interface BacktestResult {
  trades: number;
  winRate: number; // 0..1 over long steps taken
  strategyReturnPct: number;
  benchmarkReturnPct: number; // buy & hold
  maxDrawdownPct: number;
  sharpe: number; // per-step Sharpe of the strategy (not annualized — irregular data)
  equity: number[]; // strategy equity curve (starts at 1)
}

// enough points for RSI(14) plus SMA(21) slack; below this the signal is noise
export const MIN_POINTS = 21;

const SMA_SHORT = 7;
const SMA_LONG = 21;
const RSI_PERIOD = 14;
const MOMENTUM_WINDOW = 12;

/** Tunable signal thresholds (see calibrate.ts). */
export interface SignalParams {
  rsiOversold: number;
  rsiOverbought: number;
  momentumThreshold: number; // percent
}

// Defaults informed by walk-forward grid search on live testnet daily data
// (2026-08, ~240 daily bars per feed; see calibrate.ts / calibrate.run.test.ts).
// Across BNB/BTCB/ETH the overbought level 60 unanimously beat the classic 70,
// so we adopt that robust finding. Oversold/momentum varied per asset, so those
// stay at conservative defaults to avoid overfitting a short, single-regime
// (mostly downtrend) sample — the strategy's edge there was rotating to cash.
export const DEFAULT_PARAMS: SignalParams = {
  rsiOversold: 30,
  rsiOverbought: 60,
  momentumThreshold: 1,
};

export const TIMEFRAME_MS: Record<Timeframe, number> = {
  '1h': 60 * 60 * 1000,
  '4h': 4 * 60 * 60 * 1000,
  '1d': 24 * 60 * 60 * 1000,
};

/**
 * Resample irregular (event-driven Chainlink) points onto a fixed time grid so
 * that TA indicators — which assume evenly spaced candles — are statistically
 * meaningful. Each bucket takes the last observed price; empty buckets between
 * observations are forward-filled with the previous price.
 */
export function resample(points: PricePoint[], intervalMs: number): number[] {
  if (points.length === 0) return [];
  const sorted = [...points].sort((a, b) => a.t - b.t);
  const firstBucket = Math.floor(sorted[0].t / intervalMs);
  const lastBucket = Math.floor(sorted[sorted.length - 1].t / intervalMs);

  // last price seen in each bucket
  const byBucket = new Map<number, number>();
  for (const p of sorted) byBucket.set(Math.floor(p.t / intervalMs), p.price);

  const out: number[] = [];
  let carry = sorted[0].price;
  for (let b = firstBucket; b <= lastBucket; b++) {
    if (byBucket.has(b)) carry = byBucket.get(b)!;
    out.push(carry);
  }
  return out;
}

function sma(values: number[], period: number): number {
  const slice = values.slice(-Math.min(period, values.length));
  return slice.reduce((a, b) => a + b, 0) / slice.length;
}

/**
 * Standard Wilder-smoothed RSI. Seeds with a simple average over the first
 * `period` deltas, then applies Wilder's exponential smoothing across the rest.
 */
export function rsi(values: number[], period: number): number {
  if (values.length <= period) return 50;
  let gain = 0;
  let loss = 0;
  for (let i = 1; i <= period; i++) {
    const diff = values[i] - values[i - 1];
    if (diff >= 0) gain += diff;
    else loss -= diff;
  }
  let avgGain = gain / period;
  let avgLoss = loss / period;
  for (let i = period + 1; i < values.length; i++) {
    const diff = values[i] - values[i - 1];
    const up = diff > 0 ? diff : 0;
    const down = diff < 0 ? -diff : 0;
    avgGain = (avgGain * (period - 1) + up) / period;
    avgLoss = (avgLoss * (period - 1) + down) / period;
  }
  if (avgLoss === 0) return avgGain === 0 ? 50 : 100;
  const rs = avgGain / avgLoss;
  return 100 - 100 / (1 + rs);
}

function confidenceFor(dataPoints: number): Confidence {
  if (dataPoints >= SMA_LONG + 9) return 'high';
  if (dataPoints >= SMA_LONG) return 'medium';
  return 'low';
}

export function computeSignal(prices: number[], params: SignalParams = DEFAULT_PARAMS): SignalResult | null {
  if (prices.length < MIN_POINTS) return null;

  const smaShort = sma(prices, SMA_SHORT);
  const smaLong = sma(prices, SMA_LONG);
  const rsiValue = rsi(prices, RSI_PERIOD);
  const momentumBase = prices[prices.length - Math.min(MOMENTUM_WINDOW, prices.length - 1) - 1];
  const momentumPct = ((prices[prices.length - 1] - momentumBase) / momentumBase) * 100;

  let score = 0;
  const reasons: string[] = [];

  if (smaShort > smaLong) {
    score += 1;
    reasons.push('단기 이동평균이 장기 이동평균 위 — 상승 추세');
  } else if (smaShort < smaLong) {
    score -= 1;
    reasons.push('단기 이동평균이 장기 이동평균 아래 — 하락 추세');
  }

  if (rsiValue < params.rsiOversold) {
    score += 1;
    reasons.push(`RSI ${rsiValue.toFixed(0)} — 과매도 구간 (반등 가능성)`);
  } else if (rsiValue > params.rsiOverbought) {
    score -= 1;
    reasons.push(`RSI ${rsiValue.toFixed(0)} — 과매수 구간 (조정 가능성)`);
  } else {
    reasons.push(`RSI ${rsiValue.toFixed(0)} — 중립 구간`);
  }

  if (momentumPct > params.momentumThreshold) {
    score += 1;
    reasons.push(`최근 모멘텀 +${momentumPct.toFixed(2)}% — 매수세 우위`);
  } else if (momentumPct < -params.momentumThreshold) {
    score -= 1;
    reasons.push(`최근 모멘텀 ${momentumPct.toFixed(2)}% — 매도세 우위`);
  }

  const level: SignalLevel =
    score >= 2 ? 'strong-buy' : score === 1 ? 'buy' : score === -1 ? 'sell' : score <= -2 ? 'strong-sell' : 'neutral';

  return {
    level,
    score,
    rsi: rsiValue,
    smaShort,
    smaLong,
    momentumPct,
    reasons,
    confidence: confidenceFor(prices.length),
    dataPoints: prices.length,
  };
}

/**
 * Walk-forward backtest with no look-ahead: at each step the signal is computed
 * from *past-only* prices, and a long position is taken for the next step when
 * the signal is bullish (score > 0), otherwise the strategy stays in cash.
 * Returns strategy vs buy&hold performance, win rate, max drawdown and a
 * per-step Sharpe ratio.
 */
export function backtest(prices: number[], params: SignalParams = DEFAULT_PARAMS): BacktestResult | null {
  if (prices.length < MIN_POINTS + 2) return null;

  let equity = 1;
  const curve: number[] = [1];
  const stepReturns: number[] = [];
  let trades = 0;
  let wins = 0;

  for (let i = MIN_POINTS; i < prices.length - 1; i++) {
    const signal = computeSignal(prices.slice(0, i + 1), params);
    const stepReturn = (prices[i + 1] - prices[i]) / prices[i];
    const long = signal !== null && signal.score > 0;
    const realized = long ? stepReturn : 0;
    if (long) {
      trades += 1;
      if (stepReturn > 0) wins += 1;
    }
    equity *= 1 + realized;
    curve.push(equity);
    stepReturns.push(realized);
  }

  if (curve.length < 2) return null;

  let peak = curve[0];
  let maxDd = 0;
  for (const v of curve) {
    if (v > peak) peak = v;
    const dd = (peak - v) / peak;
    if (dd > maxDd) maxDd = dd;
  }

  const mean = stepReturns.reduce((a, b) => a + b, 0) / stepReturns.length;
  const variance =
    stepReturns.reduce((a, b) => a + (b - mean) * (b - mean), 0) / stepReturns.length;
  const std = Math.sqrt(variance);
  const sharpe = std === 0 ? 0 : (mean / std) * Math.sqrt(stepReturns.length);

  const benchmarkReturnPct = ((prices[prices.length - 1] - prices[MIN_POINTS]) / prices[MIN_POINTS]) * 100;

  return {
    trades,
    winRate: trades === 0 ? 0 : wins / trades,
    strategyReturnPct: (equity - 1) * 100,
    benchmarkReturnPct,
    maxDrawdownPct: maxDd * 100,
    sharpe,
    equity: curve,
  };
}
